/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowNextIcon, CheckIcon } from '@coursera/cds-icons';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import UnifiedInformationModule from 'bundles/cds-labs/components/Modules/InformationModule';
import type { InformationModuleCtaVariants } from 'bundles/cds-labs/components/Modules/InformationModule';
import { toLayoutValue } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import { isInvertedColor } from 'bundles/cds-labs/components/Modules/utils/colors';
import EnrollOrUpgradeToCourseraPlus from 'bundles/coursera-plus/components/subscriptionTiers/upgradeToPlus/EnrollOrUpgradeToCourseraPlus';
import type { PromoTemplateInformationFragment } from 'bundles/promo-template/__generated__/contentfulTypes';
import Markdown from 'bundles/promo-template/contentful/core/Markdown';
import { EXPLORE_PAGE_COURSERA_PLUS_CAMPAIGN_SLUGS } from 'bundles/promo-template/contentful/modules/constants/courseraPlusCampaignSlugs';

export type Props = {
  content: PromoTemplateInformationFragment & {
    preloadImage?: boolean;
    removeTopPadding?: boolean;
    lazyLoadImage?: boolean;
  };
  slug?: string;
};

function InformationModule({ content, slug }: Props) {
  const track = useTracker();

  if (!content) return null;
  const isCourseraPlusCampaign = EXPLORE_PAGE_COURSERA_PLUS_CAMPAIGN_SLUGS.includes(slug as string);

  const trackClick = (trackingName: ButtonName, linkTarget: string) => () => {
    track('click_button', {
      button: { name: trackingName, linkURL: linkTarget },
      pageSection: {
        sectionName: 'information',
      },
    });
  };

  const ListItem: React.LegacyFunctionComponentWithChildren<HTMLUListElement> = ({ children }) => {
    return (
      <li>
        <CheckIcon size="small" color="interactive" />
        <div>{children}</div>
      </li>
    );
  };

  const contentDescription = content.description && (
    <Typography2 variant="bodyPrimary" component="div" color="inherit">
      <Markdown source={content?.description} module={content} renderers={{ listItem: ListItem }} />
    </Typography2>
  );

  const isInverted = isInvertedColor(content.backgroundColor ?? '');
  let ctaVariant: ButtonProps['variant'] = 'secondary';
  if (content?.ctaVariant === 'ghost') {
    ctaVariant = isInverted ? 'ghostInvert' : 'ghost';
  }

  const cta =
    content.ctaText && content.ctaTarget
      ? {
          label: content.ctaText,
          target: content.ctaTarget,
          // Content string -> Expected type conversion
          variant: content.ctaVariant as InformationModuleCtaVariants,
        }
      : null;

  const secondaryCta =
    content.secondaryCtaText && content.secondaryCtaTarget
      ? {
          label: content.secondaryCtaText,
          target: content.secondaryCtaTarget,
          // Content string -> Expected type conversion
          variant: content.secondaryCtaVariant as InformationModuleCtaVariants,
        }
      : null;

  const customPrimaryCta =
    isCourseraPlusCampaign && content.ctaTarget?.includes('/courseraplus') ? (
      <EnrollOrUpgradeToCourseraPlus
        ctaLabel={content.ctaText}
        ctaVariant={ctaVariant}
        trackingName="information_primary_cta"
        ctaIcon={content?.ctaVariant === 'ghost' ? <ArrowNextIcon /> : undefined}
        trackEnrollCtaClick={trackClick('promo_template_primary_cta', content.ctaTarget as string)}
      />
    ) : undefined;

  return (
    <UnifiedInformationModule
      {...content}
      cta={cta}
      secondaryCta={secondaryCta}
      layout={toLayoutValue(content.layout)}
      description={contentDescription}
      customPrimaryCta={customPrimaryCta}
    />
  );
}

export default InformationModule;
